import { DEFAULT_SIZE, OS_RESULT_WINDOW } from 'src/constants';
import { clampQuerySize } from 'src/utils/pagination';
import type { BaseSearchParams } from 'src/utils/searchSchemas';

/**
 * Allowed search parameters that the backend expects
 */
interface AllowedParams {
  term: boolean;
  filters: boolean;
  start: boolean;
  size: boolean;
  startdate: boolean;
  enddate: boolean;
  transcript: boolean;
  include: boolean;
  exclude: boolean;
  sort: boolean;
  [key: string]: boolean;
}

/**
 * Absolute minimum characters required — used by processSearchParams as a
 * defense-in-depth guard for URL/bookmark navigation.
 */
const MIN_EXPLICIT_SEARCH_CHARS = 1;

const positiveIntOr = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value > 0 ? value : fallback;

/**
 * Process a raw search term into the syntax expected by TI API / OpenSearch.
 *
 * Rules:
 * - `"word"` or `"multi word"` → pass through (exact match, no wildcard)
 * - `-"word"` or `-"phrase"` → pass through (excluded exact)
 * - `-word` → convert to `-"word"` (TI API exclusion format)
 * - plain word → append `*` (default wildcard)
 * - already ends with `*` → leave alone
 * - Unclosed quotes are stripped before tokenizing
 *
 * @param rawTerm - The user's raw search input
 * @returns The processed term string for the backend
 */
export const processSearchTerm = (rawTerm: string): string => {
  if (!rawTerm || !rawTerm.trim()) return '';

  // Strip unclosed quotes: count quotes, if odd remove the last one
  let input = rawTerm;
  const quoteCount = (input.match(/"/g) || []).length;
  if (quoteCount % 2 !== 0) {
    const lastQuoteIndex = input.lastIndexOf('"');
    input = input.slice(0, lastQuoteIndex) + input.slice(lastQuoteIndex + 1);
  }

  // Tokenize: preserve quoted phrases (optionally prefixed with -) as atomic units
  const tokenRegex = /-?"[^"]*"|-?\S+/g;
  const tokens = input.match(tokenRegex);
  if (!tokens) return '';

  const BOOLEAN_OPS = ['AND', 'OR', 'NOT'];

  const processed = tokens.map((token, index) => {
    // Quoted token (with or without minus prefix) — pass through as-is
    if (token.includes('"')) {
      return token;
    }

    // Minus-prefixed plain word: -word → -"word"
    // Handle multiple leading dashes: treat as single minus prefix
    if (token.startsWith('-') && token.length > 1) {
      const word = token.replace(/^-+/, '');
      if (word) return `-"${word}"`;
    }

    // Boolean operators — only preserve when between other terms
    // (standalone "and" or trailing "cat AND" would be invalid query_string)
    if (BOOLEAN_OPS.includes(token.toUpperCase()) && index > 0 && index < tokens.length - 1) {
      return token.toUpperCase();
    }

    // Already wildcarded — leave alone
    if (token.endsWith('*')) {
      return token;
    }

    // Plain word — append wildcard
    return `${token}*`;
  });

  return processed.join(' ');
};

/**
 * Count the number of "searchable" characters in a term, ignoring
 * quotes, dashes, asterisks, and whitespace. Used for min-character validation.
 *
 * @param term - The raw search term
 * @returns Number of meaningful characters
 */
export const getSearchableCharCount = (term: string): number => {
  return term.replace(/["*\-\s]/g, '').length;
};

/**
 * Process typed search params from TanStack Router into OpenSearch query string.
 * This is the preferred method - use typed params from useSearch().
 *
 * Automatically applies wildcard/exclusion syntax via processSearchTerm().
 * Blocks terms with fewer than MIN_SEARCHABLE_CHARS characters (defense-in-depth
 * for URL/bookmark navigation bypassing UI guards).
 *
 * IMPORTANT: When using useSearch({ strict: false }), the search params may be
 * an empty object during route transitions (e.g., navigating from admin to library).
 * This function safely handles undefined values by using sensible defaults.
 *
 * @param params - Typed search params from useSearch()
 * @param member - Optional membership filter for galleries (galleryId)
 * @returns The processed search query string for OpenSearch
 */
export const processSearchParams = (params: BaseSearchParams, member?: string): string => {
  const sanitized = new URLSearchParams();

  // Pagination - convert page to start offset.
  // Use defaults when params are undefined (can happen during route transitions with strict: false).
  // Two-step clamp:
  //   1. If `start >= OS_RESULT_WINDOW` (e.g. stale URL like ?page=999&size=24), clamp `start`
  //      back into the last valid page so we never request beyond the OS window. Without this,
  //      `clampQuerySize` returns 0 and the backend would silently fall back to its default size,
  //      returning bogus results.
  //   2. Clamp `size` so `start + size <= OS_RESULT_WINDOW`, independent of how `size` divides
  //      the window (avoids the modulo-zero last-page bug).
  const page = positiveIntOr(params?.page, 1);
  const size = positiveIntOr(params?.size, DEFAULT_SIZE);
  const rawStart = (page - 1) * size;
  const lastValidStart = Math.max(0, Math.floor((OS_RESULT_WINDOW - 1) / size) * size);
  const start = rawStart >= OS_RESULT_WINDOW ? lastValidStart : rawStart;
  const cappedSize = clampQuerySize(size, start);
  sanitized.set('start', String(start));
  sanitized.set('size', String(cappedSize));

  // Search term with automatic wildcard/exclusion processing
  if (params?.term) {
    // Defense-in-depth: block empty terms from URL/bookmark navigation
    if (getSearchableCharCount(params.term) >= MIN_EXPLICIT_SEARCH_CHARS) {
      const term = processSearchTerm(params.term);
      if (term) {
        sanitized.set('term', term);
      }
    }
  }

  // Sort
  if (params?.sort) {
    sanitized.set('sort', params.sort);
  }

  // Filters - use optional chaining for safety during route transitions
  if (params?.filters && params.filters.length > 0) {
    for (const filter of params.filters) {
      sanitized.append('filters', filter);
    }
  }

  // Date range
  if (params?.startdate) {
    sanitized.set('startdate', params.startdate);
  }
  if (params?.enddate) {
    sanitized.set('enddate', params.enddate);
  }

  // Transcript — always send state; default is ON
  sanitized.set('transcript', params?.transcript === false ? 'false' : 'true');

  // Add membership filter for galleries, if provided
  if (member) {
    sanitized.append('filters', `membership:${member}`);
  }

  return sanitized.toString();
};

/**
 * Process a legacy search query string for OpenSearch.
 *
 * Automatically applies wildcard/exclusion syntax via processSearchTerm().
 *
 * @deprecated Use processSearchParams with typed params from useSearch() instead
 * @param search - The search query string
 * @param member - Optional membership filter for galleries
 * @returns The processed search query string
 */
export const processSearchQuery = (search: string, member?: string): string => {
  const qs = new URLSearchParams(search);
  let term = qs.get('term');
  // Process term with automatic wildcard/exclusion syntax
  if (term) {
    if (getSearchableCharCount(term) >= MIN_EXPLICIT_SEARCH_CHARS) {
      term = processSearchTerm(term);
      qs.delete('term');
      qs.append('term', term);
      search = `?${qs.toString()}`;
    } else {
      qs.delete('term');
      search = `?${qs.toString()}`;
    }
  }
  // Build a sanitized set of params that the backend expects
  // Use an object map (not Set/Array.includes) to avoid TS lib warnings
  const allowed: AllowedParams = {
    term: true,
    filters: true,
    start: true,
    size: true,
    startdate: true,
    enddate: true,
    transcript: true,
    include: true,
    exclude: true,
    sort: true,
  };

  const sanitized = new URLSearchParams();
  qs.forEach((value, key) => {
    if (allowed[key]) sanitized.append(key, value);
  });

  // Add membership filter for galleries, if provided
  if (member) {
    sanitized.append('filters', `membership:${member}`);
  }

  return sanitized.toString();
};
